import type { QueryClient } from '@tanstack/react-query';
import {
  AuditQuestionCompletionStatus,
  ResponseResult,
  type GetAuditSummaryCommandOutput,
  type QuestionResponseDetails,
  type SubQuestionResponseEntry,
} from '@amzn/gbsa-helios-typescript-client';
import { auditSummaryPredicate } from './patchAuditCachePlumbing';
import { writeCachedResponseTokens } from './questionResponseTokens';
import { normalizeToTimestamp } from 'src/utils/dateUtils';

/**
 * Wire types for the `ListAuditQuestionResponses` legacy REST
 * endpoint, mirroring `src/types/helios/QuestionResponse.ts` but
 * co-located here so the polling merger doesn't reach across the
 * v2/legacy boundary. These describe what the SERVER returns for
 * the polled list — NOT the same shape as the SDK's
 * `QuestionResponseDetails` inside `useAuditSummaryQuery`'s cache.
 * The merger below translates between the two.
 */

export interface SubResponseFromWire {
  readonly subQuestionId: string;
  readonly status: string;
  readonly result?: string;
  readonly score?: number;
  readonly respondedBy: string;
  /** Epoch-millis string per the legacy REST wire shape. */
  readonly respondedAt: string;
  readonly comment?: string;
  readonly naReason?: string;
  readonly naReasonId?: string;
  readonly mediaIds?: readonly string[];
  readonly taskIds?: readonly string[];
}

export interface AuditQuestionItemResponse {
  readonly auditId: string;
  readonly businessUnitId: string;
  readonly questionId: string;
  readonly responseStatus: string;
  readonly responseResult?: string;
  readonly responseScore?: number;
  readonly respondedBy: string;
  /** Epoch-millis string per the legacy REST wire shape. */
  readonly respondedAt: string;
  /** When the row was last touched. Used for the top-level "did
   *  anything about this question change?" gate on the polling
   *  merger. */
  readonly lastUpdatedAt: string;
  readonly lastUpdatedBy: string;
  readonly response: {
    readonly comment?: string;
    readonly naReason?: string;
    readonly naReasonId?: string;
    readonly mediaIds?: readonly string[];
    readonly taskIds?: readonly string[];
  };
  readonly subResponses: readonly SubResponseFromWire[];
}

/**
 * One-way merge of poll results (from `ListAuditQuestionResponses`)
 * into every matching `useAuditSummaryQuery` cache entry.
 *
 * Design constraints:
 *
 * 1. **Per-slot newest-wins.** Every field slot (parent-level, and
 *    each sub-response) is gated by its own `respondedAt` timestamp.
 *    If a client-side optimistic patch wrote a fresher timestamp
 *    than the poll returned, the local patch wins for that slot.
 *    This is the "async completion re-check intent" pattern —
 *    a stale poll can't clobber a save that landed while it was
 *    in flight.
 *
 * 2. **No-op when nothing changed.** If none of the polled items
 *    have a newer timestamp than the cache, the merger returns
 *    without touching the cache. React Query subscribers stay on
 *    the same reference and don't re-render. Matches V1's
 *    `pollQuestionResponsesApiThunk.fulfilled` reducer.
 *
 * 3. **No rollback.** Unlike optimistic patches, the poll is
 *    authoritative for whatever slot it wins. Callers don't need
 *    a snapshot array back.
 *
 * 4. **Definitions preserved.** The merger only touches
 *    `responseDetails.mainQuestionResponse` and
 *    `responseDetails.subQuestionResponses` — question definitions,
 *    templates, media metadata, and audit-level fields are left
 *    exactly as-is.
 */
export function mergePolledResponsesIntoSummaryCache(
  queryClient: QueryClient,
  auditId: string,
  items: readonly AuditQuestionItemResponse[],
): void {
  if (items.length === 0) return;

  // Sidecar tokens are written unconditionally — even when the poll
  // finds no summary-cache matches (e.g. a page that consumes the
  // polling data but doesn't hold the summary open). The mutation's
  // onMutate reads from here to enrich `expectedLastUpdatedAt`.
  writeCachedResponseTokens(
    queryClient,
    auditId,
    items.map((item) => [item.questionId, item.lastUpdatedAt] as const),
  );

  const matchingQueries = queryClient.getQueriesData<GetAuditSummaryCommandOutput>({
    predicate: auditSummaryPredicate(auditId),
  });
  if (matchingQueries.length === 0) return;

  const itemsByQuestionId = new Map<string, AuditQuestionItemResponse>(
    items.map((item) => [item.questionId, item]),
  );

  for (const [queryKey, cachedData] of matchingQueries) {
    const nextData = mergeIntoOne(cachedData, itemsByQuestionId);
    if (nextData !== cachedData) {
      queryClient.setQueryData(queryKey, nextData);
    }
  }
}

/**
 * Return next cache shape for one summary entry, or the input by
 * reference if nothing changed. Reference-equal return is the signal
 * to the caller that no `setQueryData` write is needed.
 */
function mergeIntoOne(
  cachedData: GetAuditSummaryCommandOutput | undefined,
  itemsByQuestionId: ReadonlyMap<string, AuditQuestionItemResponse>,
): GetAuditSummaryCommandOutput | undefined {
  if (!cachedData?.questions) return cachedData;

  let anyChanged = false;
  const nextQuestions = cachedData.questions.map((question) => {
    const questionId = question.questionDetails?.questionId;
    if (!questionId) return question;
    const item = itemsByQuestionId.get(questionId);
    if (!item) return question;
    const nextResponseDetails = mergeResponseDetails(question.responseDetails, item);
    if (nextResponseDetails === question.responseDetails) return question;
    anyChanged = true;
    return { ...question, responseDetails: nextResponseDetails };
  });

  if (!anyChanged) return cachedData;
  return { ...cachedData, questions: nextQuestions };
}

/**
 * Merge one polled item into an existing `responseDetails` blob.
 * Parent and sub slots are gated independently — see design
 * constraint #1.
 */
function mergeResponseDetails(
  previous: GetAuditSummaryCommandOutput['questions'][number]['responseDetails'],
  item: AuditQuestionItemResponse,
): GetAuditSummaryCommandOutput['questions'][number]['responseDetails'] {
  const nextMain = mergeParentResponse(previous?.mainQuestionResponse, item);
  const nextSubs = mergeSubResponses(
    previous?.subQuestionResponses ?? [],
    item.subResponses,
  );
  const mainChanged = nextMain !== previous?.mainQuestionResponse;
  const subsChanged = nextSubs !== previous?.subQuestionResponses;
  if (!mainChanged && !subsChanged) return previous;
  return {
    ...previous,
    mainQuestionResponse: nextMain ?? previous?.mainQuestionResponse,
    // Cast to mutable — the SDK types this slot as
    // `SubQuestionResponseEntry[]` even though we treat it as
    // readonly internally. Runtime shape is identical; the cast
    // only unbinds the compile-time readonly modifier.
    subQuestionResponses: nextSubs as SubQuestionResponseEntry[],
  };
}

/**
 * Newest-wins gate on the PARENT-level response. Returns the input
 * `previous` by reference when the poll's timestamp is not newer
 * than the cached timestamp — so structural sharing kicks in and no
 * subscriber re-renders.
 */
function mergeParentResponse(
  previous: QuestionResponseDetails | undefined,
  item: AuditQuestionItemResponse,
): QuestionResponseDetails | undefined {
  const polledMs = normalizeToTimestamp(item.respondedAt);
  const previousMs = previous?.respondedAt
    ? normalizeToTimestamp(previous.respondedAt)
    : 0;
  if (polledMs <= previousMs) return previous;
  return buildParentResponseFromItem(item, polledMs);
}

function buildParentResponseFromItem(
  item: AuditQuestionItemResponse,
  respondedAtMs: number,
): QuestionResponseDetails {
  // Cast: the SDK requires `responseResult`, but the wire allows
  // undefined for a never-answered top-level (aggregated result on
  // sub layouts is derived). Matches the treatment in patch
  // helpers that also intermittently omit this field.
  return {
    responseStatus: item.responseStatus as AuditQuestionCompletionStatus,
    responseResult: item.responseResult as ResponseResult | undefined,
    responseScore: item.responseScore ?? 0,
    respondedBy: item.respondedBy,
    respondedAt: new Date(respondedAtMs),
    ...(item.response?.comment !== undefined
      ? { responseComment: item.response.comment }
      : {}),
    ...(item.response?.naReason !== undefined
      ? { naReason: item.response.naReason }
      : {}),
    ...(item.response?.mediaIds !== undefined
      ? { mediaIds: item.response.mediaIds }
      : {}),
    ...(item.response?.taskIds !== undefined ? { taskIds: item.response.taskIds } : {}),
  } as unknown as QuestionResponseDetails;
}

/**
 * Merge the sub-response array. Each sub is independently gated by
 * its own `respondedAt` — a polled SQ1 that's newer wins even if
 * cached SQ2 is fresher than polled SQ2. Return-by-reference when
 * no sub changed.
 */
function mergeSubResponses(
  previous: readonly SubQuestionResponseEntry[],
  polledSubs: readonly SubResponseFromWire[],
): readonly SubQuestionResponseEntry[] {
  if (polledSubs.length === 0) return previous;

  const polledBySubId = new Map<string, SubResponseFromWire>(
    polledSubs.map((s) => [s.subQuestionId, s]),
  );

  let anyChanged = false;
  // Start from the cached list so we preserve subs that weren't in
  // the poll (defence: shouldn't happen for the same auditId, but
  // keeps the invariant that we NEVER drop a cached sub).
  const seenIds = new Set<string>();
  const nextByIndex = previous.map((entry) => {
    seenIds.add(entry.subQuestionId);
    const polled = polledBySubId.get(entry.subQuestionId);
    if (!polled) return entry;
    const merged = mergeOneSubResponse(entry, polled);
    if (merged !== entry) anyChanged = true;
    return merged;
  });

  // Sub-responses the poll returned but the cache didn't have yet —
  // append them at the end. Only build entries whose polled
  // `respondedAt` is > 0 (skip seed/placeholder rows).
  const additions: SubQuestionResponseEntry[] = [];
  for (const polled of polledSubs) {
    if (seenIds.has(polled.subQuestionId)) continue;
    const respondedAtMs = normalizeToTimestamp(polled.respondedAt);
    if (respondedAtMs <= 0) continue;
    additions.push({
      subQuestionId: polled.subQuestionId,
      response: buildSubResponse(polled, respondedAtMs),
    });
    anyChanged = true;
  }

  if (!anyChanged) return previous;
  return additions.length > 0 ? [...nextByIndex, ...additions] : nextByIndex;
}

function mergeOneSubResponse(
  previous: SubQuestionResponseEntry,
  polled: SubResponseFromWire,
): SubQuestionResponseEntry {
  const polledMs = normalizeToTimestamp(polled.respondedAt);
  const previousMs = previous.response?.respondedAt
    ? normalizeToTimestamp(previous.response.respondedAt)
    : 0;
  if (polledMs <= previousMs) return previous;
  return {
    ...previous,
    response: buildSubResponse(polled, polledMs),
  };
}

function buildSubResponse(
  polled: SubResponseFromWire,
  respondedAtMs: number,
): QuestionResponseDetails {
  return {
    responseStatus: polled.status as AuditQuestionCompletionStatus,
    responseResult: polled.result as ResponseResult | undefined,
    responseScore: polled.score ?? 0,
    respondedBy: polled.respondedBy,
    respondedAt: new Date(respondedAtMs),
    ...(polled.comment !== undefined ? { responseComment: polled.comment } : {}),
    ...(polled.naReason !== undefined ? { naReason: polled.naReason } : {}),
    ...(polled.mediaIds !== undefined ? { mediaIds: polled.mediaIds } : {}),
    ...(polled.taskIds !== undefined ? { taskIds: polled.taskIds } : {}),
  } as unknown as QuestionResponseDetails;
}
